import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import {
  idSchema,
  isoDateSchema,
  isValidIsin,
  reasonSchema,
  revisionSchema,
  text,
} from '../validators'
import {
  MAPPING_EXCEPTION_STATUSES,
  type MappingExceptionStatus,
  type MappingState,
  SECURITY_IDENTIFIER_TYPES,
  type SecurityIdentifierType,
} from '../vocab'
import { clientContextQuerySchema } from './coverage'

/**
 * Portfolios, holdings, the holdings CSV import and mapping exceptions (M4,
 * P-REC-003, P-COV-002, IO-001..014, GAP-DD-002).
 */

// --- Portfolios -------------------------------------------------------------

const currencySchema = z
  .string()
  .regex(/^[A-Z]{3}$/, { message: 'Use a three-letter ISO 4217 currency code.' })

export const portfolioCreateSchema = z.strictObject({
  name: text(2, 120),
  description: text(0, 1000).nullish(),
  base_currency: currencySchema.nullish(),
})
export type PortfolioCreate = z.infer<typeof portfolioCreateSchema>

export const portfolioUpdateSchema = z
  .strictObject({
    revision: revisionSchema,
    name: text(2, 120).optional(),
    description: text(0, 1000).nullish(),
    base_currency: currencySchema.nullish(),
  })
  .refine(
    (v) => v.name !== undefined || v.description !== undefined || v.base_currency !== undefined,
    { message: 'Change the name, description or currency.' },
  )
export type PortfolioUpdate = z.infer<typeof portfolioUpdateSchema>

export const portfolioDeleteSchema = z.strictObject({ revision: revisionSchema })

export type Portfolio = {
  id: string
  name: string
  description: string | null
  base_currency: string | null
  holdings: number
  latest_as_of: string | null
  revision: number
  created_at: string
  updated_at: string
}

// --- Holdings ---------------------------------------------------------------

/** Typed identifiers are upper-cased; client codes keep their case. */
export function normalizeIdentifier(type: string, value: string): string {
  const trimmed = value.trim()
  return type === 'OTHER' || type === 'PROVIDER_ID' ? trimmed : trimmed.toUpperCase()
}

/** VAL-016/017 for ISINs; other types need 1-64 printable characters. */
export function identifierIssue(type: string, value: string): string | null {
  if (!(SECURITY_IDENTIFIER_TYPES.values as readonly string[]).includes(type)) {
    return `Identifier type must be one of ${SECURITY_IDENTIFIER_TYPES.values.join(', ')}.`
  }
  if (value.length === 0) return 'Security identifier is required.'
  if (value.length > 64) return 'Security identifier must be at most 64 characters.'
  if (type === 'ISIN' && !isValidIsin(value)) return 'ISIN is not valid.'
  return null
}

const weightSchema = z
  .number()
  .min(0, { message: 'weight must be between 0 and 100.' })
  .max(100, { message: 'weight must be between 0 and 100.' })

export const holdingCreateSchema = z
  .strictObject({
    portfolio_id: idSchema,
    identifier_type: z.enum(SECURITY_IDENTIFIER_TYPES.values),
    identifier_value: text(1, 64),
    as_of: isoDateSchema,
    weight: weightSchema.nullish(),
    issuer_name_hint: text(0, 200).nullish(),
  })
  .transform((v) => ({
    ...v,
    identifier_value: normalizeIdentifier(v.identifier_type, v.identifier_value),
  }))
  .superRefine((v, ctx) => {
    const issue = identifierIssue(v.identifier_type, v.identifier_value)
    if (issue) ctx.addIssue({ code: 'custom', path: ['identifier_value'], message: issue })
  })
export type HoldingCreate = z.infer<typeof holdingCreateSchema>

export const holdingUpdateSchema = z
  .strictObject({
    revision: revisionSchema,
    weight: weightSchema.nullish(),
    issuer_name_hint: text(0, 200).nullish(),
  })
  .refine((v) => v.weight !== undefined || v.issuer_name_hint !== undefined, {
    message: 'Change the weight or the issuer name.',
  })
export type HoldingUpdate = z.infer<typeof holdingUpdateSchema>

export const holdingDeleteSchema = z.strictObject({ revision: revisionSchema })

export const holdingListQuerySchema = pageQuerySchema
  .extend(clientContextQuerySchema.shape)
  .extend({
    portfolio: idSchema.optional(),
    mapping: z.enum(['verified', 'needs review', 'unresolved', 'out of scope']).optional(),
    query: z.string().max(180).optional(),
  })

export type Holding = {
  id: string
  portfolio_id: string
  portfolio: string
  identifier_type: SecurityIdentifierType | string
  identifier_value: string
  issuer_name_hint: string | null
  security_id: string | null
  issuer_id: string | null
  company: string | null
  mapping_state: MappingState
  as_of: string
  weight: number | null
  import_batch_id: string | null
  revision: number
  created_at: string
  updated_at: string
}

// --- CSV import (P-REC-003, IO-001..014) ------------------------------------

export const HOLDINGS_IMPORT_MAX_ROWS = 5000
export const HOLDINGS_IMPORT_MAX_BYTES = 2 * 1024 * 1024
export const HOLDINGS_IMPORT_REQUIRED_HEADERS = [
  'security_identifier',
  'identifier_type',
  'portfolio',
  'as_of',
] as const
export const HOLDINGS_IMPORT_OPTIONAL_HEADERS = ['issuer_name', 'issuer_id', 'weight'] as const

export const holdingsImportQuerySchema = clientContextQuerySchema.extend({
  /** Import every row into this portfolio; the `portfolio` column is then optional. */
  portfolio_id: idSchema.optional(),
  /** Validate and report without writing anything. */
  dry_run: z
    .enum(['true', 'false', '1', '0'])
    .optional()
    .transform((v) => v === 'true' || v === '1'),
})

export type HoldingsImportRowError = {
  /** 1-based file line; the header is line 1 (IO-006). */
  row: number
  column?: string
  reason: string
}

/** `error.details` of a rejected import (IO-010): nothing was written. */
export type HoldingsImportErrorDetails = {
  input_rows: number
  accepted_rows: 0
  errors: HoldingsImportRowError[]
  /** True when more errors exist than are listed. */
  truncated?: boolean
}

export type HoldingsImportResult = {
  import_batch_id: string | null
  dry_run: boolean
  input_rows: number
  accepted_rows: number
  created_holdings: number
  existing_holdings: number
  mapped_rows: number
  unresolved_rows: number
  created_portfolios: string[]
  mapping_exceptions_created: number
  errors: []
}

// --- Mapping exceptions (P-COV-002, GAP-UI-021) -----------------------------

export const MAPPING_RESOLUTIONS = ['link', 'out_of_scope', 'dismiss'] as const
export type MappingResolution = (typeof MAPPING_RESOLUTIONS)[number]

export const mappingExceptionResolveSchema = z.discriminatedUnion('action', [
  z.strictObject({
    action: z.literal('link'),
    revision: revisionSchema,
    security_id: idSchema,
    note: reasonSchema,
  }),
  z.strictObject({
    action: z.literal('out_of_scope'),
    revision: revisionSchema,
    note: reasonSchema,
  }),
  z.strictObject({ action: z.literal('dismiss'), revision: revisionSchema, note: reasonSchema }),
])
export type MappingExceptionResolve = z.infer<typeof mappingExceptionResolveSchema>

export const mappingExceptionListQuerySchema = pageQuerySchema
  .extend(clientContextQuerySchema.shape)
  .extend({ status: z.enum(MAPPING_EXCEPTION_STATUSES.values).optional() })

export const mappingCandidatesQuerySchema = clientContextQuerySchema.extend({
  q: z.string().max(120).optional(),
})

export type MappingException = {
  id: string
  holding_id: string
  portfolio_id: string
  portfolio: string
  as_of: string
  identifier_type: string
  identifier_value: string
  issuer_name_hint: string | null
  reason: 'unresolved' | 'needs_review' | 'conflict'
  status: MappingExceptionStatus
  holding_mapping_state: MappingState
  resolved_security_id: string | null
  resolved_company: string | null
  resolved_by_name: string | null
  resolved_at: string | null
  resolution_note: string | null
  created_at: string
  revision: number
}

/** A canonical security a manager can link an exception to. */
export type MappingCandidate = {
  security_id: string
  identifier_type: string
  identifier_value: string
  name: string | null
  issuer_id: string
  company: string
  country: string
  sector: string
  mapping_state: string
}
